import { IconButton, makeStyles } from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'
import { LatLng, LatLngTuple } from 'leaflet'
import React, { useMemo, useState } from 'react'
import { Rectangle } from 'react-leaflet'

import { getOverridenEntityProperty } from 'src/parser/EntitiesCustomization'
import { GeoBoundingBox, IMapManifest } from '@obvious.tech/constellation'
import { isFiniteNumber } from '@obvious.tech/util/number'
import Map from '../Map/Map'
import ObjectInput from './ObjectInput'
import { TEntityOverrideProp, IInputProps } from 'src/types'
import { selectDefinitions } from 'src/redux/definitions/definitions.selectors'
import { useAppSelector } from 'src/redux/createAdminStore'

const useStyles = makeStyles({
  flex: {
    display: 'flex',
  },
  coordinateInputs: {
    margin: 8,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  map: {
    height: '100%',
    width: '100%',
  },
})

const getBounds = (bbox: GeoBoundingBox | undefined): LatLngTuple[] => {
  if (typeof bbox !== 'object') return []

  const { north, south, east, west } = bbox
  if (
    !isFiniteNumber(north) ||
    !isFiniteNumber(east) ||
    !isFiniteNumber(south) ||
    !isFiniteNumber(west)
  )
    return []

  return [
    [north, east],
    [south, west],
  ]
}

const MapManifestInput = ({
  name,
  property,
  record,
  onChange,
  value,
  classes,
  ...rest
}: IInputProps<IMapManifest>): JSX.Element => {
  const ownClasses = useStyles()
  const mapManifestDefinition = useAppSelector(
    state => selectDefinitions(state).objects?.MapManifest,
  )
  const { north, south, east, west } = value?.boundingBox ?? {}

  const [bounds, setBounds] = useState<LatLngTuple[]>(() =>
    getBounds(value?.boundingBox),
  )

  const mapManifestProperty = useMemo((): TEntityOverrideProp => {
    if (
      typeof mapManifestDefinition !== 'object' ||
      typeof mapManifestDefinition.properties !== 'object'
    )
      return {}

    return Object.keys(mapManifestDefinition.properties).reduce((acc, key) => {
      acc.properties ??= {}
      acc.properties[key] = getOverridenEntityProperty<IMapManifest>(
        mapManifestDefinition,
        key,
      )
      return acc
      // eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter, @typescript-eslint/no-explicit-any
    }, structuredClone(mapManifestDefinition) as any)
  }, [mapManifestDefinition])

  const onClickMap = (e: { latlng: LatLng }): void => {
    const { lat, lng } = e.latlng
    if (bounds?.length === undefined || bounds.length === 0) {
      setBounds([[lat, lng]])
    } else if (bounds.length === 1) {
      setBounds([bounds[0], [lat, lng]])

      onChange({
        ...value,
        // @ts-expect-error should be interface and not class
        boundingBox: {
          north: lat > bounds[0][0] ? lat : bounds[0][0],
          east: lng > bounds[0][1] ? lng : bounds[0][1],
          south: lat < bounds[0][0] ? lat : bounds[0][0],
          west: lng < bounds[0][1] ? lng : bounds[0][1],
        },
      })
    }
  }
  return (
    <div className={classes}>
      <label>{property.displayedName}</label>
      <div className={ownClasses.flex}>
        <Map
          className={ownClasses.map}
          center={
            isFiniteNumber(north) &&
            isFiniteNumber(south) &&
            isFiniteNumber(east) &&
            isFiniteNumber(west)
              ? {
                  lat: (north + south) / 2,
                  lng: (east + west) / 2,
                }
              : {
                  lat: 48,
                  lng: 2,
                }
          }
          // length={4}
          zoom={13}
          onclick={onClickMap}
        >
          {bounds.length === 2 && (
            <Rectangle
              bounds={bounds}
              color='black'
            />
          )}
        </Map>
        <div className={ownClasses.coordinateInputs}>
          <div>
            <IconButton
              aria-label='clear'
              color='primary'
              onClick={() => {
                onChange(undefined)
                setBounds([])
              }}
            >
              <ClearIcon />
            </IconButton>
          </div>
          <ObjectInput<IMapManifest>
            value={value}
            onChange={onChange}
            // TODO: Improve surrounding types to fix
            // @ts-ignore
            property={mapManifestProperty}
            name={'Map manifest'}
          />
        </div>
      </div>
    </div>
  )
}

export default MapManifestInput
